"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.EntityList = EntityList;
const DataTable_1 = require("@juvare/common-data-table-ui/DataTable");
const FiltersBar_1 = require("@juvare/common-data-table-ui/FiltersBar");
const DialogHelper_1 = require("@juvare/common-web-ui/DialogHelper");
const NavigationContext_1 = require("@juvare/common-web-ui/NavigationContext");
const PageHeader_1 = require("@juvare/common-web-ui/PageHeader");
const CsvFile_1 = require("@juvare/data-import-ui/CsvFile");
const hooks_1 = require("../../../../../../ui/hooks");
const usePlatformSdkReactTranslation_1 = require("../../../../../../ui/hooks/usePlatformSdkReactTranslation");
const Add_1 = __importDefault(require("@mui/icons-material/Add"));
const FileDownloadOutlined_1 = __importDefault(require("@mui/icons-material/FileDownloadOutlined"));
const FileUploadOutlined_1 = __importDefault(require("@mui/icons-material/FileUploadOutlined"));
const material_1 = require("@mui/material");
const react_1 = __importDefault(require("react"));
const react_router_1 = require("react-router");
const useEntityList_1 = require("../hooks/useEntityList");
const changeStatus_1 = require("../utils/changeStatus");
const exportAsCsv_1 = require("../utils/exportAsCsv");
const handleImportFromCsv_1 = require("../utils/handleImportFromCsv");
const EntitySlideout_1 = require("./EntitySlideout");
function EntityList(props) {
    const { t } = (0, usePlatformSdkReactTranslation_1.usePlatformSdkReactTranslation)();
    const navigationContext = react_1.default.useContext(NavigationContext_1.NavigationContext);
    const navigate = (0, react_router_1.useNavigate)();
    const storage = (0, hooks_1.useStorageClient)(props.entity);
    const { dataTableProps, filtersBarProps, appliedFilterData, clearActiveRow, reload } = (0, DataTable_1.useDataTableUsingUrl)({
        useDataProvider: (request) => (0, useEntityList_1.useEntityList)(props, request),
        onRowClick: () => undefined,
        rowSelectionEnabled: true,
        defaultSortColumnDesc: props.defaultSortColumnDesc,
        defaultSortColumnKey: props.defaultSortColumnKey?.toString() ?? 'name',
        maxUrlSearchLength: 200,
    });
    const columns = react_1.default.useMemo(() => props.tableColumns ?? [
        {
            headerLabel: t('common.name'),
            sortKey: 'name',
            cellFunction: (row) => react_1.default.createElement(DataTable_1.TextCell, null, row.columns.name),
        },
        {
            headerLabel: t('common.status'),
            sortKey: 'status',
            cellFunction: (row) => react_1.default.createElement(DataTable_1.TextCell, null, t(`classifiers.statusEnum.${row.columns.status}`)),
        },
    ], [props.tableColumns]);
    const { fullTextSearchField, buildFilter } = props;
    const filterConfig = (0, FiltersBar_1.useFilterConfig)((builder) => {
        if (fullTextSearchField) {
            builder.quickSearchFilter({ dataPath: fullTextSearchField, label: t('common.search') });
        }
        if (buildFilter) {
            buildFilter(builder);
        }
    }, [fullTextSearchField, buildFilter]);
    const handleExportAsCsv = (all) => {
        (0, DialogHelper_1.showProgressDialog)({
            title: t('classifiers.exportingCsvFile'),
            callback: (done) => {
                (0, exportAsCsv_1.exportAsCsv)(props, storage, dataTableProps.selectedRowIds, appliedFilterData, dataTableProps.sortColumnKey, dataTableProps.sortColumnDesc, all)
                    .then((dataUrl) => {
                    (0, DialogHelper_1.showAlertDialog)({
                        title: t('classifiers.exportComplete'),
                        content: (react_1.default.createElement(material_1.Box, { p: 4, sx: { display: 'flex', justifyContent: 'center' } },
                            react_1.default.createElement(material_1.Button, { variant: 'contained', color: 'primary', download: getExportedCsvFileName(t, props.title), href: dataUrl }, t('classifiers.downloadExportedCsvFile')))),
                    });
                })
                    .catch(DialogHelper_1.handleErrorDialog)
                    .finally(done);
            },
        });
    };
    const handleStatusChange = (title, status) => () => {
        (0, DialogHelper_1.showProgressDialog)({
            title: title,
            callback: (done) => (0, changeStatus_1.changeStatus)(props, storage, status, dataTableProps.selectedRowIds, appliedFilterData, dataTableProps.sortColumnKey, dataTableProps.sortColumnDesc)
                .catch(DialogHelper_1.handleErrorDialog)
                .finally(() => {
                done();
                reload();
            }),
        });
    };
    const downloadTemplateLink = react_1.default.useRef(null);
    const templateCsvFileDataUrl = (0, CsvFile_1.stringifyCsvFileToDataUrl)(generateCsvFile(props.csvFileHeader ?? [], props.csvRowExample ?? undefined));
    return (react_1.default.createElement(material_1.Box, { sx: { height: '100%', overflow: 'auto' } },
        react_1.default.createElement(PageHeader_1.PageHeaderWithImage, { gradient: 'blue-base', title: props.title }),
        react_1.default.createElement("a", { ref: downloadTemplateLink, style: { display: 'none' }, download: t('classifiers.templateFileName'), href: templateCsvFileDataUrl }, t('classifiers.downloadTemplate')),
        react_1.default.createElement(material_1.Box, { p: 4 },
            react_1.default.createElement(DataTable_1.DataTable, { ...dataTableProps, title: props.title, columns: columns, mainAction: props.hideCreate
                    ? undefined
                    : {
                        icon: react_1.default.createElement(Add_1.default, null),
                        label: t('common.new'),
                        onClick: () => navigationContext.navigate(() => navigate('new', { state: { back: true } })),
                    }, secondaryAction: props.csvFileHeader
                    ? {
                        icon: react_1.default.createElement(FileUploadOutlined_1.default, null),
                        label: t('common.import'),
                        menuItems: [
                            {
                                label: t('classifiers.downloadTemplate'),
                                onClick: () => downloadTemplateLink.current?.click(),
                                'data-test': 'download-template-button',
                            },
                            {
                                label: t('common.fromFile'),
                                onClick: () => (0, handleImportFromCsv_1.handleImportFromCsv)(t, props, storage, reload),
                            },
                        ],
                    }
                    : undefined, globalActions: [
                    ...(props.csvFileHeader
                        ? [
                            {
                                icon: react_1.default.createElement(FileDownloadOutlined_1.default, null),
                                label: t('common.exportAll'),
                                onClick: () => handleExportAsCsv(true),
                            },
                        ]
                        : []),
                    ...(props.additionalGlobalActions ?? []),
                ], localActions: [
                    ...(props.csvFileHeader
                        ? [
                            {
                                icon: react_1.default.createElement(FileDownloadOutlined_1.default, null),
                                label: t('common.export'),
                                onClick: () => handleExportAsCsv(false),
                            },
                        ]
                        : []),
                    {
                        label: t('classifiers.setAsActive'),
                        onClick: handleStatusChange(t('classifiers.changingStatusToActive'), 'active'),
                    },
                    {
                        label: t('classifiers.setAsRetired'),
                        onClick: handleStatusChange(t('classifiers.changingStatusToRetired'), 'retired'),
                    },
                ], filtersBar: react_1.default.createElement(FiltersBar_1.FiltersBar, { ...filtersBarProps, config: filterConfig, onFilterDebounceDelay: 0 }), tableSx: { tableLayout: 'fixed' } })),
        react_1.default.createElement(EntitySlideout_1.EntitySlideout, { ...props, id: dataTableProps.activeRowId, onClose: (changed) => {
                clearActiveRow();
                if (changed) {
                    reload();
                }
            } })));
}
function getExportedCsvFileName(t, title) {
    const name = title.toLowerCase().replace(/\s+/g, '_');
    const timestamp = new Date().toISOString().replace(/\..*$/, '').replace(/[-:]/g, '').replace(/T/, '_');
    return t('classifiers.exportedCsvFileName', { name, timestamp });
}
function generateCsvFile(header, csvRowExample) {
    if (csvRowExample !== undefined) {
        return {
            header: header,
            rows: [csvRowExample],
        };
    }
    return {
        header: header,
        rows: [],
    };
}
